/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    Loader,
    preventDefault,
    SelectItemShape
} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {
    FocusEventHandler,
    KeyboardEventHandler,
    MouseEventHandler,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {useTranslation} from 'react-i18next';

import {UserRight, UserRightItem, UserRole} from '../../../../util/interfaces';

import Invitation from '../../../../models/invitationModel';

import AccountUser from '../../../../models/accountUserModel';

import {BolmateButton, BolmateInput, BolmateSelect} from '../../../controls';

import {getAvailableUserRights, translateUserRole} from '../../../../util/accessUtil';

interface SharedProps {
    onSubmit: MouseEventHandler & KeyboardEventHandler;
    onClose: MouseEventHandler;
    onChangeRight: (right: UserRight) => ChangeFunction<boolean>;
    onChangeRightGroup: (rightItem: UserRightItem) => ChangeFunction<boolean>;
    onChangeObject: ChangeKeyFunction;
    onChangeRole: ChangeFunction<UserRole>;
    obj: Invitation | AccountUser;
    loading: boolean;
    active: boolean;
}

export function AccountUserSettings(props: SharedProps) {
    const {t} = useTranslation();
    const {obj, loading} = props;

    function renderUserDetails() {
        const name = obj instanceof Invitation ? obj.email : obj.fullName;
        return (
            <div className='user-details'>
                <h2>{name}</h2>
            </div>
        );
    }

    return (
        <form className='account-user' onSubmit={preventDefault}>
            <Loader active={loading} />
            {renderUserDetails()}
            <RightsSelection {...props} submitText={t('settings.accountUsers.save', 'Opslaan')} />
        </form>
    );
}

interface NewInvitationProps extends SharedProps {
    onEnterEmail: FocusEventHandler;
    obj: Invitation;
    emailAlreadyAdded: boolean;
}

export function NewInvitationForm(props: NewInvitationProps) {
    const {t} = useTranslation();
    const {obj, onChangeObject, onEnterEmail, loading, emailAlreadyAdded} = props;

    function renderEmailConfirmation() {
        if (!emailAlreadyAdded) return null;
        if (emailAlreadyAdded)
            return (
                <label className='user-exists error'>
                    <AwesomeIcon name='times-circle' className='check-icon' />{' '}
                    {t(
                        'settings.accountUsers.userAlreadyHasAccess',
                        'Deze gebruiker heeft al toegang tot het account.'
                    )}
                </label>
            );
    }

    return (
        <form className='new-invitation' onSubmit={preventDefault}>
            <Loader active={loading} />
            <div className='email-row'>
                <BolmateInput
                    onChange={onChangeObject('email')}
                    value={obj.email}
                    label={t('settings.accountUsers.email', 'E-mailadres')}
                    inputProps={{placeholder: t('settings.accountUsers.email', 'E-mailadres'), type: 'email'}}
                    onBlur={onEnterEmail}
                    id='invitation-mail'
                />
                {renderEmailConfirmation()}
            </div>
            <RightsSelection
                {...props}
                submitText={
                    <span>{t('settings.accountUsers.addUserForPrice', 'Gebruiker toevoegen voor +€5/maand')}</span>
                }
                submitDisabled={!obj.email || emailAlreadyAdded}
            />
        </form>
    );
}

function findRight(rightItem: UserRightItem, rights: Array<UserRight>): UserRight {
    return rights.find((right: UserRight) => right.key === rightItem.key) ?? {...rightItem, allow: false};
}

interface RightsSelectionProps extends SharedProps {
    submitText: ReactNode;
    submitDisabled?: boolean;
}

function RightsSelection(props: RightsSelectionProps) {
    const {t} = useTranslation();
    const availableUserRights = useMemo(() => getAvailableUserRights(), [t]);
    const rightsTabs = useMemo(() => Object.keys(availableUserRights), [availableUserRights]);
    const USER_ROLE_SELECT_ITEMS: Array<SelectItemShape> = useMemo(
        () => [
            {label: t('settings.accountUsers.chooseRole', 'Kies rol...'), value: '', selectable: false},
            {
                label: (
                    <div className='user-role-item'>
                        <label className='user-role-label'>{translateUserRole(UserRole.CUSTOMER_SERVICE)}</label>
                        <div className='user-role-info'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='info-message' style={{width: '20rem'}}>
                                {t(
                                    'settings.accountUsers.roleCustomerServiceInfo',
                                    'Heeft geen toegang tot plekken met financiële cijfers (sales dashboard, btw hulptool, exports, etc). Kan enkel factuuracties uitvoeren.'
                                )}
                            </p>
                        </div>
                    </div>
                ),
                value: UserRole.CUSTOMER_SERVICE,
                valueLabel: translateUserRole(UserRole.CUSTOMER_SERVICE)
            },
            {
                label: (
                    <div className='user-role-item'>
                        <label className='user-role-label'>{translateUserRole(UserRole.EMPLOYEE)}</label>
                        <div className='user-role-info'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='info-message' style={{width: '20rem'}}>
                                {t(
                                    'settings.accountUsers.roleEmployeeInfo',
                                    'Kan alles behalve gebruikers toevoegen/verwijderen, abonnement stoppen of verkoopaccount verwijderen'
                                )}
                            </p>
                        </div>
                    </div>
                ),
                value: UserRole.EMPLOYEE,
                valueLabel: translateUserRole(UserRole.EMPLOYEE)
            },
            {
                label: (
                    <div className='user-role-item'>
                        <label className='user-role-label'>{translateUserRole(UserRole.ACCOUNTANT)}</label>
                        <div className='user-role-info top'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='info-message' style={{width: '20rem'}}>
                                {t(
                                    'settings.accountUsers.roleAccountantInfo',
                                    'Heeft alleen-lezen rechten. Kan wel bekijken, downloaden en exporten, maar kan geen wijzigingen doorvoeren.'
                                )}
                            </p>
                        </div>
                    </div>
                ),
                value: UserRole.ACCOUNTANT,
                valueLabel: translateUserRole(UserRole.ACCOUNTANT)
            },
            {
                label: (
                    <div className='user-role-item'>
                        <label className='user-role-label'>{translateUserRole(UserRole.ADMIN)}</label>
                        <div className='user-role-info top'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='info-message' style={{width: '20rem'}}>
                                {t(
                                    'settings.accountUsers.roleAdminInfo',
                                    'Heeft alle rechten, ook gebruikers toevoegen en verwijderen.'
                                )}
                            </p>
                        </div>
                    </div>
                ),
                value: UserRole.ADMIN,
                valueLabel: translateUserRole(UserRole.ADMIN)
            }
        ],
        [t]
    );

    const {
        obj,
        onChangeRight,
        onChangeRightGroup,
        onChangeRole,
        loading,
        onSubmit,
        active,
        submitText,
        submitDisabled
    } = props;
    const [activeTab, setActiveTab] = useState<string>(rightsTabs[0]);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // rightsTabs is derived from translated keys, so when the user switches language the
    // currently-selected tab string no longer exists on the new availableUserRights object.
    // Snap back to the first tab so downstream lookups don't blow up.
    useEffect(() => {
        if (!rightsTabs.includes(activeTab)) setActiveTab(rightsTabs[0]);
    }, [rightsTabs, activeTab]);

    function onToggleOpen() {
        setOpen(!open);
    }

    function onChangeActiveTab(tab: string) {
        return function () {
            if (tab !== activeTab) setActiveTab(tab);
            containerRef.current?.scrollTo({top: 0, behavior: 'smooth'});
        };
    }

    function renderHeader() {
        return (
            <div className='user-rights-header' onClick={onToggleOpen}>
                <div className='line' />
                <label className='user-rights-header-label'>
                    {t('settings.accountUsers.detailed', 'Gedetailleerd')}
                </label>
                <AwesomeIcon name='chevron-up' className='chev-icon' />
            </div>
        );
    }

    function renderRole() {
        const role = obj.userRole;
        return (
            <div className='role-container'>
                <BolmateSelect
                    onChange={onChangeRole}
                    value={role ?? ''}
                    items={USER_ROLE_SELECT_ITEMS}
                    label={t('settings.accountUsers.role', 'Rol')}
                    highlightSelected
                />
            </div>
        );
    }

    function renderTabs() {
        return <div className='tabs-container'>{rightsTabs.map(renderTab)}</div>;
    }

    function renderTab(tab: string, index: number) {
        const isActive = activeTab === tab;
        const allRights = availableUserRights[tab];
        const activeRights = allRights.reduce((prev: number, right: UserRightItem) => {
            let next: number = prev;
            if (right.children)
                for (const child of right.children) {
                    if (findRight(child, obj.rights).allow) next++;
                }
            else if (findRight(right, obj.rights).allow) next++;
            return next;
        }, 0);
        return (
            <div key={index} className={classNames('tab', isActive && 'active')} onClick={onChangeActiveTab(tab)}>
                <label className='tab-label'>
                    {tab} ({activeRights})
                </label>
            </div>
        );
    }

    function renderRights() {
        const rights = availableUserRights[activeTab] ?? [];
        const half = Math.ceil(rights.length / 2);
        const leftCol = rights.slice(0, half);
        const rightCol = rights.slice(half);
        return (
            <div className='rights-container'>
                <div className='rights-col'>{leftCol.map(renderRight(false))}</div>
                <div className='rights-col'>{rightCol.map(renderRight(false))}</div>
            </div>
        );
    }

    function getRightValue(item: UserRightItem, right: UserRight): boolean | 'partial' {
        if (!item.children) return right.allow;
        let hasTrue: boolean = false,
            hasFalse: boolean = false;
        for (const child of item.children) {
            const _right = findRight(child, obj.rights);
            if (_right.allow) hasTrue = true;
            else hasFalse = true;
            if (hasTrue && hasFalse) return 'partial';
        }
        return hasTrue;
    }

    function renderRight(hasParent: boolean) {
        return function (rightItem: UserRightItem) {
            const userRight = findRight(rightItem, obj.rights);
            const hasChildren = (rightItem.children?.length ?? 0) > 0;
            const groupValue = getRightValue(rightItem, userRight);
            return (
                <div
                    key={rightItem.key}
                    className={classNames('right-container', hasParent && 'has-parent', hasChildren && 'has-children')}
                >
                    {hasChildren ? (
                        <Checkbox
                            checked={groupValue}
                            onCheck={onChangeRightGroup(rightItem)}
                            label={rightItem.label}
                        />
                    ) : (
                        <Checkbox
                            checked={userRight.allow}
                            onCheck={onChangeRight(userRight)}
                            label={rightItem.label}
                        />
                    )}
                    {rightItem.children?.map(renderRight(true))}
                </div>
            );
        };
    }

    function renderFooter() {
        const disabled = loading || submitDisabled || !obj.userRole;
        return (
            <div className='footer-container'>
                <BolmateButton onClick={onSubmit} label={submitText} disabled={disabled} />
            </div>
        );
    }

    useEffect(() => {
        // Restore first tab after 200ms (closing transition)
        if (!active) {
            window.setTimeout(setActiveTab, 200, rightsTabs[0]);
            setOpen(false);
        }
    }, [active]);

    return (
        <div className={classNames('user-rights-container', !open && 'closed')}>
            {renderRole()}
            {renderHeader()}
            {renderTabs()}
            {renderRights()}
            {renderFooter()}
        </div>
    );
}
